import { useApolloClient } from "@apollo/client";
import { Modal, notification } from "antd";
import dayjs from "dayjs";
import { debounce, get } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaUpload } from "react-icons/fa";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import SelectAsync from "shared/components/Select/Async";
import { NAME_SPACES } from "shared/locales/constants";
import { ORGANIZATIONS } from "utils/api/graphql/queries/organizations";
import { SUBSCRIPTION_CLIENT_OPTIONS } from "utils/api/graphql/queries/subscription";
import { DATE_FORMAT, SUBSCRIPTION_STATUSES } from "utils/constants";
import { resolveApiErrorMessage } from "utils/helpers/graphql-errors";
import { formatClientName } from "../clientName";

const contractOptionsLimit = 20;
const searchDelayMs = 300;

const buildContractOptionsVariables = (search, organizationId) => {
  const where = {
    OR: [
      { status: SUBSCRIPTION_STATUSES.SIGNED },
      { status: SUBSCRIPTION_STATUSES.COMPLETED },
    ],
  };
  const variables = {
    where: organizationId
      ? {
          AND: [where, { project: { ownedBy: { id: organizationId } } }],
        }
      : where,
    take: contractOptionsLimit,
  };
  if (!search) {
    return variables;
  }
  return {
    ...variables,
    like: {
      OR: ["firstname", "lastname"].map((field) => ({
        project: { contact: { user: { [field]: `%${search}%` } } },
      })),
    },
  };
};

const toDate = (value) =>
  value ? (dayjs.isDayjs(value) ? value.toDate() : value) : null;

const Ciblemut = ({ open, onClose, action, organizationFilter }) => {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.SUBSCRIPTION);
  const { t: e } = useTranslation(NAME_SPACES.ERRORS);
  const CIBLEMUT = t("LIST.HEADER.EXTERNAL_EXPORT.CIBLEMUT", {
    returnObjects: true,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [contract, setContract] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStartDate(null);
      setEndDate(null);
      setOrganizationId(null);
      setContract(null);
      setSearch("");
      setLoading(false);
    }
  }, [open]);

  const onSearch = useMemo(
    () => debounce((value) => setSearch((value || "").trim()), searchDelayMs),
    [],
  );

  useEffect(() => () => onSearch.cancel(), [onSearch]);

  const hasDateError =
    !!startDate &&
    !!endDate &&
    dayjs(toDate(endDate)).isBefore(dayjs(toDate(startDate)), "day");
  const hasCriteria =
    !!startDate || !!endDate || !!contract || !!organizationId;

  const notifyResult = (result) => {
    if (!get(result, "enabled")) {
      notification.warning({ message: CIBLEMUT.DISABLED });
      return;
    }
    if (get(result, "running")) {
      notification.warning({ message: CIBLEMUT.RUNNING });
      return;
    }
    if (!get(result, "count")) {
      notification.info({ message: CIBLEMUT.EMPTY });
      return;
    }
    notification.success({
      message: CIBLEMUT.SUCCESS_TITLE,
      description: t("LIST.HEADER.EXTERNAL_EXPORT.CIBLEMUT.SUCCESS_MESSAGE", {
        count: get(result, "count"),
        environment: get(result, "environment"),
      }),
      duration: 10,
    });
  };

  const onSubmit = async () => {
    if (hasDateError) {
      return;
    }
    const clauses = [];
    if (organizationId) {
      clauses.push({ project: { ownedBy: { id: organizationId } } });
    }
    if (contract) {
      clauses.push({ id: contract.value });
    }
    const variables = {};
    if (clauses.length === 1) {
      variables.where = clauses[0];
    } else if (clauses.length > 1) {
      variables.where = { AND: clauses };
    }
    if (startDate) {
      variables.from = dayjs(toDate(startDate)).startOf("day").toISOString();
    }
    if (endDate) {
      variables.to = dayjs(toDate(endDate)).endOf("day").toISOString();
    }
    setLoading(true);
    try {
      notifyResult(await action({ client, variables }));
      onClose();
    } catch (error) {
      notification.error({
        message: resolveApiErrorMessage(
          error,
          e("API", { returnObjects: true }),
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal__container"
      open={open}
      onCancel={loading ? null : onClose}
      centered
      footer={[
        <Button
          key="cancel"
          type="primary--link"
          onClick={onClose}
          icon={<FaChevronLeft />}
          disabled={loading}
        >
          {CIBLEMUT.CANCEL}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          icon={<FaUpload />}
          disabled={hasDateError}
          loading={loading}
        >
          {CIBLEMUT.SUBMIT}
        </Button>,
      ]}
    >
      <div className="modal__container--body">
        <h3>{CIBLEMUT.TITLE}</h3>
        <div className="export-modal__content">
          <p className="export-modal__message">{CIBLEMUT.MESSAGE}</p>
          <div className="export-modal__field">
            <SelectAsync
              allowClear
              optional
              label={CIBLEMUT.ORGANIZATION_LABEL}
              placeholder={CIBLEMUT.ORGANIZATION_PLACEHOLDER}
              node={{
                query: ORGANIZATIONS,
                variables: {
                  where: {
                    active: true,
                    ...organizationFilter,
                  },
                  take: 1000,
                },
                accessors: {
                  root: "organizations.data",
                  label: "name",
                  value: "id",
                },
              }}
              value={organizationId}
              onChange={(value) => {
                setOrganizationId(value || null);
                setContract(null);
                setSearch("");
              }}
            />
          </div>
          <div className="export-modal__field">
            <SelectAsync
              allowClear
              optional
              labelInValue
              filterOption={false}
              label={CIBLEMUT.CONTRACT}
              placeholder={CIBLEMUT.CONTRACT_PLACEHOLDER}
              node={{
                query: SUBSCRIPTION_CLIENT_OPTIONS,
                variables: buildContractOptionsVariables(
                  search,
                  organizationId,
                ),
                accessors: {
                  root: "subscriptions.data",
                  label: (subscription) =>
                    formatClientName(get(subscription, "project")),
                  value: "id",
                },
              }}
              value={contract}
              onSearch={onSearch}
              onChange={(value) => setContract(value || null)}
            />
          </div>
          <div className="export-modal__field">
            <Input
              type="date"
              label={CIBLEMUT.START_DATE}
              placeholder={CIBLEMUT.START_DATE_PLACEHOLDER}
              value={startDate}
              onChange={(date) => setStartDate(date || null)}
              format={DATE_FORMAT}
              disabledDate={(current) =>
                !!endDate &&
                !!current &&
                dayjs(current).isAfter(dayjs(toDate(endDate)), "day")
              }
              errors={hasDateError ? CIBLEMUT.DATE_ERROR : undefined}
              touched={hasDateError}
            />
          </div>
          <div className="export-modal__field">
            <Input
              type="date"
              label={CIBLEMUT.END_DATE}
              placeholder={CIBLEMUT.END_DATE_PLACEHOLDER}
              value={endDate}
              onChange={(date) => setEndDate(date || null)}
              format={DATE_FORMAT}
              disabledDate={(current) =>
                !!startDate &&
                !!current &&
                dayjs(current).isBefore(dayjs(toDate(startDate)), "day")
              }
              errors={hasDateError ? CIBLEMUT.DATE_ERROR : undefined}
              touched={hasDateError}
            />
          </div>
          {!hasCriteria && (
            <p className="export-modal__count-info">
              {CIBLEMUT.DEFAULT_PERIOD_HINT}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

Ciblemut.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  action: PropTypes.func.isRequired,
  organizationFilter: PropTypes.object,
};

Ciblemut.defaultProps = {
  open: false,
  organizationFilter: {},
};

export default Ciblemut;
